
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { EyeIcon, TrashIcon, ArrowPathIcon, CogIcon, PlayIcon, StopIcon, AdjustmentsHorizontalIcon, WifiIcon } from '@heroicons/react/24/outline';
import SmartTrackingSettingsPanel from './SmartTrackingSettingsPanel';
import RealTimeTrackingDashboard from './RealTimeTrackingDashboard';
import TrackingSyncPanel from './TrackingSyncPanel';

// Yardımcı Fonksiyonlar
const formatDuration = (startTime) => {
    if (!startTime) return 'N/A';
    const start = new Date(startTime);
    const now = new Date();
    const diffMs = now - start;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}g ${diffHours % 24}s`;
    if (diffHours > 0) return `${diffHours}s ${diffMins % 60}dk`;
    return `${diffMins}dk`;
};

const getPhaseInfo = (phase) => {
    const phases = {
        reference: { label: 'Referans Bekliyor', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
        tracking: { label: 'Takip No Bekliyor', color: 'bg-blue-100 text-blue-800 border-blue-200' },
        status: { label: 'Durum Takibi', color: 'bg-purple-100 text-purple-800 border-purple-200' },
        completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-800 border-green-200' },
        failed: { label: 'Başarısız', color: 'bg-red-100 text-red-800 border-red-200' },
    };
    return phases[phase] || { label: phase, color: 'bg-gray-100 text-gray-800 border-gray-200' };
};

const safeFormatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'Geçersiz Tarih' : date.toLocaleString('tr-TR');
};

function AutoTrackingPanel() {
    const { token } = useAuth();
    const [serviceStatus, setServiceStatus] = useState(null);
    const [activePolls, setActivePolls] = useState([]);
    const [completedPolls, setCompletedPolls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [showHistory, setShowHistory] = useState(null); // log id
    const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, settings, realtime, sync

    const fetchData = useCallback(async () => {
        if (!token) return;
        setRefreshing(true);
        try {
            const [statusRes, activeRes, completedRes] = await Promise.all([
                api.get('/api/smart-tracking/status', { headers: { Authorization: `Bearer ${token}` } }),
                api.get('/api/smart-tracking/active', { headers: { Authorization: `Bearer ${token}` } }),
                api.get('/api/smart-tracking/completed', { headers: { Authorization: `Bearer ${token}` } })
            ]);

            console.log('[AutoTrackingPanel] API Yanıtları:');
            console.log('Status:', statusRes);
            console.log('Active:', activeRes);
            console.log('Completed:', completedRes);

            // Status endpoint: { success, data }
            if (statusRes && statusRes.success) {
                setServiceStatus(statusRes.data || null);
            }

            // Active/completed endpointleri: { success, count, items }
            const activeItems = (activeRes && activeRes.success && Array.isArray(activeRes.items)) ? activeRes.items : [];
            const completedItems = (completedRes && completedRes.success && Array.isArray(completedRes.items)) ? completedRes.items : [];

            setActivePolls(activeItems);
            setCompletedPolls(completedItems);
        } catch (error) {
            toast.error('Takip verileri alınamadı: ' + error.message);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [token]);

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 30000); // 30 saniyede bir yenile
        return () => clearInterval(interval);
    }, [fetchData]);

    const handleServiceControl = async (action) => {
        try {
            const response = await api.post(`/api/smart-tracking/${action}-service`, {}, { headers: { Authorization: `Bearer ${token}` } });
            if (response.success) {
                toast.success(response.message);
                fetchData();
            }
        } catch (error) {
            toast.error(`İşlem başarısız: ${error.message}`);
        }
    };

    const handleDeleteLog = async (logId) => {
        if (!window.confirm('Bu takip kaydını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) return;
        try {
            // Bu endpoint'i routes/autoTrackingService.js'e eklememiz gerekecek
            const response = await api.delete(`/api/smart-tracking/log/${logId}`, { headers: { Authorization: `Bearer ${token}` } });
            if (response.success) {
                toast.success('Takip kaydı silindi.');
                fetchData();
            }
        } catch (error) {
            toast.error(`Silme işlemi başarısız: ${error.message}`);
        }
    };

    const handleAutoSync = async () => {
        try {
            toast.info('Otomatik senkronizasyon başlatılıyor...');
            const response = await api.post('/api/auto-sync/trigger', {}, { headers: { Authorization: `Bearer ${token}` } });
            if (response.success) {
                toast.success(`Senkronizasyon tamamlandı! ${response.data.started} takip başlatıldı.`);
                fetchData(); // Verileri yenile
            }
        } catch (error) {
            toast.error(`Otomatik senkronizasyon başarısız: ${error.message}`);
        }
    };

    if (loading && !serviceStatus) {
        return <div>Yükleniyor...</div>;
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg">
                            <CogIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Akıllı Takip Sistemi</h2>
                            <p className="text-gray-600">Hibrit takip sistemi - Real-time + Akıllı Polling</p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3">
                        {/* Tab Navigation */}
                        <div className="flex bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => setActiveTab('dashboard')}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                                    activeTab === 'dashboard'
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                Dashboard
                            </button>
                            <button
                                onClick={() => setActiveTab('settings')}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                                    activeTab === 'settings'
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                <AdjustmentsHorizontalIcon className="w-4 h-4 mr-2 inline" />
                                Ayarlar
                            </button>
                            <button
                                onClick={() => setActiveTab('realtime')}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                                    activeTab === 'realtime'
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                <WifiIcon className="w-4 h-4 mr-2 inline" />
                                Real-time
                            </button>
                            <button
                                onClick={() => setActiveTab('sync')}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                                    activeTab === 'sync'
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                <ArrowPathIcon className="w-4 h-4 mr-2 inline" />
                                Senkronizasyon
                            </button>
                        </div>
                        
                        {activeTab === 'dashboard' && (
                            <>
                                <button onClick={() => handleServiceControl('start')} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center">
                                    <PlayIcon className="h-4 w-4 mr-2" />
                                    Başlat
                                </button>
                                <button onClick={() => handleServiceControl('stop')} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center">
                                    <StopIcon className="h-4 w-4 mr-2" />
                                    Durdur
                                </button>
                                <button onClick={fetchData} disabled={refreshing} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center">
                                    <ArrowPathIcon className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                                    Yenile
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'settings' ? (
                <SmartTrackingSettingsPanel />
            ) : activeTab === 'realtime' ? (
                <RealTimeTrackingDashboard />
            ) : activeTab === 'sync' ? (
                <TrackingSyncPanel />
            ) : (
                <>
                {/* Servis Durumu */}
                <div className="bg-white rounded-2xl shadow-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Servis Durumu</h3>
                {serviceStatus && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-gray-100 p-4 rounded-lg">
                            <p className="text-sm font-medium text-gray-600">Servis Durumu</p>
                            <p className={`font-bold ${serviceStatus.isRunning ? 'text-green-600' : 'text-red-600'}`}>{serviceStatus.isRunning ? 'Çalışıyor' : 'Durduruldu'}</p>
                        </div>
                        <div className="bg-gray-100 p-4 rounded-lg">
                            <p className="text-sm font-medium text-gray-600">Aktif Takipler</p>
                            <p className="font-bold text-blue-600">{serviceStatus.activePollsCount}</p>
                        </div>
                        <div className="bg-gray-100 p-4 rounded-lg">
                            <p className="text-sm font-medium text-gray-600">Son Güncelleme</p>
                            <p className="font-bold text-gray-600">{new Date().toLocaleTimeString('tr-TR')}</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Aktif Takipler */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Aktif Takipler ({Array.isArray(activePolls) ? activePolls.length : 0})</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                    {(Array.isArray(activePolls) ? activePolls : []).length > 0 ? (activePolls || []).map((poll, idx) => (
                        <div key={poll.id || `${poll.order_id}-${idx}`} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                    <span className="font-mono text-sm font-medium text-gray-900">Sipariş #{poll.order_id}</span>
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPhaseInfo(poll.phase).color}`}>{getPhaseInfo(poll.phase).label}</span>
                                    <span className="text-sm text-gray-600">Deneme: {poll.attempts ?? 0}</span>
                                    <span className="text-sm text-gray-600">Süre: {formatDuration(poll.started_at || poll.created_at)}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button onClick={() => setShowHistory(poll)} className="p-2 rounded-full hover:bg-gray-200"><EyeIcon className="h-5 w-5 text-gray-500" /></button>
                                    <button onClick={() => handleDeleteLog(poll.id)} className="p-2 rounded-full hover:bg-red-100"><TrashIcon className="h-5 w-5 text-red-500" /></button>
                                </div>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                Son Kontrol: {safeFormatDate(poll.last_check_at)} | Sonraki Kontrol: {safeFormatDate(poll.next_check_at)}
                            </div>
                            {poll.last_message && <div className="text-xs text-gray-600 mt-1">Mesaj: {poll.last_message}</div>}
                        </div>
                    )) : <p className="text-gray-500">Aktif takip bulunmuyor.</p>}
                </div>
            </div>
            
            {/* Tamamlanan Takipler */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Son Tamamlanan Takipler ({Array.isArray(completedPolls) ? completedPolls.length : 0})</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                    {(Array.isArray(completedPolls) ? completedPolls : []).length > 0 ? (completedPolls || []).map((poll, idx) => (
                        <div key={poll.id || `${poll.order_id}-completed-${idx}`} className="bg-gray-50 rounded-xl p-4 border border-gray-200 opacity-80">
                             <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                    <span className="font-mono text-sm font-medium text-gray-700">Sipariş #{poll.order_id}</span>
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPhaseInfo(poll.status).color}`}>{getPhaseInfo(poll.status).label}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button onClick={() => setShowHistory(poll)} className="p-2 rounded-full hover:bg-gray-200"><EyeIcon className="h-5 w-5 text-gray-500" /></button>
                                    <button onClick={() => handleDeleteLog(poll.id)} className="p-2 rounded-full hover:bg-red-100"><TrashIcon className="h-5 w-5 text-red-500" /></button>
                                </div>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">Tamamlanma: {safeFormatDate(poll.updated_at)}</div>
                            {poll.last_message && <div className="text-xs text-gray-600 mt-1">Mesaj: {poll.last_message}</div>}
                        </div>
                    )) : <p className="text-gray-500">Tamamlanmış takip bulunmuyor.</p>}
                </div>
            </div>

            {/* Geçmiş Modal */}
            {showHistory && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowHistory(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Sipariş #{showHistory.order_id || showHistory?.orderId} Takip Geçmişi</h3>
                        <div className="space-y-2">
                            {/* Eğer poll nesnesinde history yoksa, sunucudan canlı çek */}
                            {Array.isArray(showHistory?.history) && showHistory.history.length > 0 ? (
                                showHistory.history.map((entry, index) => (
                                    <div key={index} className={`p-3 rounded-lg ${entry.error ? 'bg-red-50' : 'bg-green-50'}`}>
                                        <p className="text-sm font-medium">{entry.message}</p>
                                        <p className="text-xs text-gray-500">{safeFormatDate(entry.timestamp)}</p>
                                    </div>
                                ))
                            ) : (
                                <HistoryLoader orderId={showHistory.order_id || showHistory?.orderId} />
                            )}
                        </div>
                        <button onClick={() => setShowHistory(null)} className="mt-4 px-4 py-2 bg-gray-200 rounded-lg">Kapat</button>
                    </div>
                </div>
            )}
                </>
            )}
        </div>
    );
}

/* Yardımcı alt bileşen: HistoryLoader */
function HistoryLoader({ orderId }) {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const { token } = useAuth();

    useEffect(() => {
        let mounted = true;
        const run = async () => {
            try {
                const res = await api.get(`/api/smart-tracking/history/${orderId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (mounted) {
                    const history = Array.isArray(res?.data?.history) ? res.data.history : (res?.data?.data?.history || []);
                    setItems(history);
                }
            } catch (e) {
                // yok say
            } finally {
                mounted = false;
                setLoading(false);
            }
        };
        if (orderId) run();
        return () => { mounted = false; };
    }, [orderId, token]);

    if (loading) return <div className="text-sm text-gray-500">Geçmiş yükleniyor...</div>;
    if (!items || items.length === 0) return <div className="text-sm text-gray-500">Geçmiş kayıt bulunamadı.</div>;

    return (
        <div className="space-y-2">
            {items.map((entry, index) => (
                <div key={index} className={`p-3 rounded-lg ${entry.error ? 'bg-red-50' : 'bg-green-50'}`}>
                    <p className="text-sm font-medium">{entry.message}</p>
                    <p className="text-xs text-gray-500">{safeFormatDate(entry.timestamp)}</p>
                </div>
            ))}
        </div>
    );
}

export default AutoTrackingPanel;
